import {
  Building2,
  CalendarDays,
  Globe,
  Mail,
  Phone,
  Users,
} from "lucide-react";

import type { PilotRequest } from "@/api/platform";
import { PlatformCard } from "@/components/platform/ui/PlatformCard";
import { PlatformSectionHeader } from "@/components/platform/ui/PlatformSectionHeader";
import { PlatformStatusBadge } from "@/components/platform/ui/PlatformStatusBadge";

type PilotRequestDetailsProps = {
  request: PilotRequest | null;
  onApprove: (request: PilotRequest) => void;
};

export function PilotRequestDetails({
  request,
  onApprove,
}: PilotRequestDetailsProps) {
  if (!request) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <p className="text-slate-500">
          Select a pilot request.
        </p>
      </div>
    );
  }

  const submittedDate =
    request.submitted_at ?? request.created_at;

  return (
    <div className="space-y-6">
      <PlatformSectionHeader
        title={request.church_name}
        description={`Submitted ${formatDateTime(submittedDate)}`}
        action={
          <PlatformStatusBadge status={request.status} />
        }
      />

      <PlatformCard>
        <PlatformSectionHeader
          title="Church Information"
          icon={<Building2 className="h-5 w-5" />}
        />

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Info
            label="Church"
            value={request.church_name}
          />

          <Info
            label="Current System"
            value={
              request.current_system ||
              "Not provided"
            }
          />

          <Info
            label="Church Size"
            value={request.church_size || "Not provided"}
          />

          <Info
            label="Request Source"
            value={formatLabel(request.source)}
          />
        </div>
      </PlatformCard>

      <PlatformCard>
        <PlatformSectionHeader
          title="Primary Contact"
          icon={<Mail className="h-5 w-5" />}
        />

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Info
            label="Contact"
            value={request.contact_name}
          />

          <Info
            label="Email"
            value={request.contact_email}
          />

          <Info
            label="Phone"
            value={request.contact_phone || "Not provided"}
            icon={<Phone className="h-4 w-4" />}
          />
        </div>
      </PlatformCard>

      <PlatformCard>
        <PlatformSectionHeader
          title="Pilot Goals"
          icon={<Globe className="h-5 w-5" />}
        />

        <div className="mt-6 space-y-6">
          <Info
            label="Primary Challenge"
            value={
              request.primary_challenge ||
              "No primary challenge was provided."
            }
          />

          <Info
            label="Applicant Notes"
            value={
              request.notes ||
              "No additional notes were provided."
            }
          />
        </div>
      </PlatformCard>

      <PlatformCard>
        <PlatformSectionHeader
          title="Request Timeline"
          icon={<CalendarDays className="h-5 w-5" />}
        />

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Info
            label="Created"
            value={formatDateTime(request.created_at)}
          />

          <Info
            label="Last Updated"
            value={formatDateTime(request.updated_at)}
          />

          {request.reviewed_at && (
            <Info
              label="Reviewed"
              value={formatDateTime(request.reviewed_at)}
            />
          )}

          {request.approved_at && (
            <Info
              label="Approved"
              value={formatDateTime(request.approved_at)}
            />
          )}

          {request.converted_at && (
            <Info
              label="Converted"
              value={formatDateTime(request.converted_at)}
            />
          )}
        </div>
      </PlatformCard>

      <PlatformCard>
        <PlatformSectionHeader
          title="Internal Notes"
          icon={<Users className="h-5 w-5" />}
        />

        <textarea
          key={request.id}
          rows={6}
          defaultValue={request.internal_notes ?? ""}
          placeholder="Add notes about this church..."
          className="mt-6 w-full rounded-xl border border-slate-200 p-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
        />

        <p className="mt-2 text-xs text-slate-500">
          Saving internal notes will be connected in the next
          workflow step.
        </p>
      </PlatformCard>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => onApprove(request)}
          disabled={
            request.status === "DECLINED" ||
            request.status === "CONVERTED"
          }
          className="rounded-xl bg-indigo-600 px-5 py-3 font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {request.status === "APPROVED"
            ? "Provision Church"
            : "Approve"}
        </button>

        <button
          type="button"
          disabled
          title="This workflow will be connected next."
          className="rounded-xl border border-amber-300 bg-amber-50 px-5 py-3 font-semibold text-amber-700 opacity-60"
        >
          Request Information
        </button>

        <button
          type="button"
          disabled
          title="This workflow will be connected next."
          className="rounded-xl border border-rose-300 bg-rose-50 px-5 py-3 font-semibold text-rose-700 opacity-60"
        >
          Decline
        </button>
      </div>
    </div>
  );
}

function Info({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </p>

      <div className="mt-2 flex items-start gap-2 text-sm leading-6 text-slate-900">
        {icon}
        <span className="whitespace-pre-wrap">{value}</span>
      </div>
    </div>
  );
}

function formatDateTime(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatLabel(value: string): string {
  return value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map(
      (part) =>
        part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join(" ");
}